import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import Loader from "@/components/Loader";

const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  icon,
  searchable = false,
  categories = [],
  showCategories = false, // New prop to control categories visibility
  renderOption,
  className = "",
  error,
  dropdownWidth = "w-full",
  triggerClassName = "",
  multiSelect = false, // New prop for multi-select functionality
  selectedValues = [], // New prop for selected values in multi-select mode
  allowAll = false,
  allLabel = "All",
  serverUrl = null,
  isLoading = false,
  loadingText = "Loading...",
  sortBy = "alphabetical", // New prop for custom sorting: 'alphabetical', 'numeric', or custom function
  disabled = false,
  customArrow = null, // New prop for custom arrow icon
  menuMinWidth = 0,
  menuAlign = "left",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null); // New ref for the trigger div
  const menuRef = useRef(null);
  const [ddwidth, setDDWidth] = useState(dropdownWidth);
  const [isAbove, setIsAbove] = useState(false);

  useEffect(() => {
    const calculateWidth = () => {
      if (
        (!dropdownWidth || dropdownWidth == "w-full" || dropdownWidth == "") &&
        dropdownRef.current?.parentElement
      ) {
        const parentWidth = dropdownRef.current.parentElement.offsetWidth;
        setDDWidth(`${parentWidth}px`);
      }
    };

    // Calculate width on initial render
    calculateWidth();

    // Add resize listener for window resize
    const handleResize = () => {
      calculateWidth();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const triggerWidth = triggerRef.current.offsetWidth;
    const explicitWidth =
      typeof dropdownWidth === "number"
        ? dropdownWidth
        : typeof dropdownWidth === "string" && dropdownWidth.endsWith("px")
          ? parseInt(dropdownWidth, 10) || 0
          : 0;
    const width = Math.max(
      triggerWidth,
      explicitWidth,
      Number(menuMinWidth) || 0,
    );
    setDDWidth(`${width}px`);
  }, [dropdownWidth, isOpen, menuMinWidth]);

  // Filter options based on search query and category
  useEffect(() => {
    const hasGroups = options.some(
      (option) => typeof option === "object" && option.isGroup,
    );
    const query = searchQuery?.toLowerCase() || "";

    let filtered = [];

    if (hasGroups) {
      let pendingGroup = null;
      options.forEach((option) => {
        if (typeof option === "object" && option.isGroup) {
          pendingGroup = option;
          return;
        }

        const label =
          typeof option === "object" ? option.label || "" : String(option);
        if (searchable && query && !label.toLowerCase().includes(query)) {
          return;
        }

        if (pendingGroup) {
          filtered.push(pendingGroup);
          pendingGroup = null;
        }
        filtered.push(option);
      });
    } else {
      filtered = options;

      if (searchQuery && searchable) {
        filtered = filtered.filter((option) =>
          typeof option === "object"
            ? option.label?.toLowerCase().includes(query)
            : option.toLowerCase().includes(query),
        );
      }

      if (showCategories && selectedCategory !== "all" && categories.length > 1) {
        filtered = filtered.filter(
          (option) => option.category === selectedCategory,
        );
      }

      filtered = [...filtered].sort((a, b) => {
        const aLabel = typeof a === "object" ? a.label : a;
        const bLabel = typeof b === "object" ? b.label : b;

        if (sortBy === "numeric") {
          const aNum = parseInt(aLabel) || 0;
          const bNum = parseInt(bLabel) || 0;
          return aNum - bNum;
        } else if (typeof sortBy === "function") {
          return sortBy(a, b);
        }

        return aLabel.toLowerCase().localeCompare(bLabel.toLowerCase());
      });
    }

    const finalOptions = allowAll
      ? [{ label: allLabel, value: "" }, ...filtered]
      : filtered;

    if (JSON.stringify(finalOptions) !== JSON.stringify(filteredOptions)) {
      setFilteredOptions(finalOptions);
    }
  }, [
    searchQuery,
    selectedCategory,
    options,
    searchable,
    categories,
    showCategories,
    allowAll,
    allLabel,
    sortBy,
    filteredOptions,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update positioning effect
  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      // If there's not enough space below but more space above, position above
      if (spaceBelow < menuRect.height && spaceAbove > spaceBelow) {
        menuRef.current.style.bottom = "100%";
        menuRef.current.style.top = "auto";
        menuRef.current.style.marginTop = "0";
        menuRef.current.style.marginBottom = "0.25rem";
        setIsAbove(true);
      } else {
        menuRef.current.style.top = "100%";
        menuRef.current.style.bottom = "auto";
        menuRef.current.style.marginTop = "0.25rem";
        menuRef.current.style.marginBottom = "0";
        setIsAbove(false);
      }
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option?.isGroup) return;

    if (multiSelect) {
      // For multi-select, toggle the selection
      const isSelected = selectedValues.some((item) =>
        typeof item === "object" && typeof option === "object"
          ? item.value === option.value
          : item === option,
      );

      const newSelectedValues = isSelected
        ? selectedValues.filter((item) =>
            typeof item === "object" && typeof option === "object"
              ? item.value !== option.value
              : item !== option,
          )
        : [...selectedValues, option];

      onChange(newSelectedValues);

      // Don't close dropdown on selection in multi-select mode
      setSearchQuery("");
    } else {
      // For single-select, behave as before
      onChange(option);
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  // Function to handle checkbox click
  const handleCheckboxClick = (e, option) => {
    e.stopPropagation();
    handleSelect(option);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery("");
      setFilteredOptions(options);
    }
  };

  // Handle option display
  const getOptionLabel = (option) => {
    if (typeof option === "object" && option !== null) {
      return option.label || option.name || JSON.stringify(option);
    }
    return option;
  };

  // Check if an option is selected in multi-select mode
  const isOptionSelected = (option) => {
    if (!multiSelect || !selectedValues?.length) return false;

    return selectedValues.some((item) =>
      typeof item === "object" && typeof option === "object"
        ? item.value === option.value
        : item === option,
    );
  };

  return (
    <>
      <div className={`${className} relative`} ref={dropdownRef}>
        {/* Dropdown trigger */}
        {isLoading && (
          <Loader
            loadingText={loadingText}
            containerClassName="w-full max-h-[50px]"
            className="!h-[45px]"
          />
        )}
        {!isLoading && (
          <>
            <div
              ref={triggerRef} // Attach ref here
              onClick={() => !disabled && toggleDropdown()}
              className={`flex items-center justify-between gap-2 bg-muted text-foreground rounded-lg p-3 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} border ${
                error
                  ? "border-red-500"
                  : "border-input hover:border-muted-foreground"
              } transition-all ${triggerClassName}`}
            >
              <div className="flex items-center gap-2 w-full overflow-hidden min-w-0">
                {icon && <span className="flex-shrink-0">{icon}</span>}
                {multiSelect && selectedValues?.length > 0 ? (
                  <span className="truncate">
                    {placeholder} ({selectedValues.length})
                  </span>
                ) : (
                  <span className="truncate">
                    {value ? getOptionLabel(value) : placeholder}
                  </span>
                )}
              </div>
              {customArrow ? (
                <div
                  className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                >
                  {customArrow}
                </div>
              ) : (
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              )}
            </div>

            {/* Error message */}
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

            {/* Dropdown menu */}
            {isOpen && (
              <div
                ref={menuRef}
                className={`absolute z-[9999] bg-white rounded-lg shadow-lg border border-[#e6f5ef] overflow-hidden flex flex-col ${
                  menuAlign === "right" ? "right-0" : "left-0"
                }`}
                style={{ width: ddwidth, minWidth: ddwidth }}
              >
                {/* Categories - Only shown if showCategories is true */}
                {showCategories && categories.length > 1 && !isAbove && (
                  <div className="p-2 border-b border-border flex gap-2 overflow-x-auto sticky top-0 bg-card z-10">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedCategory === category
                            ? "bg-primary text-white"
                            : "bg-muted text-foreground hover:bg-secondary-soft"
                        }`}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Search bar - Show at top if dropdown is below, at bottom if above */}
                {searchable && !isAbove && (
                  <div className="p-2 border-b border-border sticky top-0 bg-card z-10">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-foreground w-4 h-4" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="pl-8 pr-2 py-2 w-full bg-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand text-foreground text-sm"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Options list */}
                <div className="overflow-y-auto overflow-x-hidden max-h-80">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      if (option?.isGroup) {
                        return (
                          <div
                            key={option.value || index}
                            className="px-3 py-2 bg-[#F8FAFC] border-b border-[#e6f5ef]"
                          >
                            <p className="text-xs font-semibold text-[#344054] leading-4">
                              {getOptionLabel(option)}
                            </p>
                          </div>
                        );
                      }

                      return (
                      <div
                        key={index}
                        onClick={() => handleSelect(option)}
                        className={`px-3 py-2.5 hover:bg-[#F4F4F5] cursor-pointer border-b border-[#e6f5ef] last:border-b-0 transition-colors ${
                          multiSelect && isOptionSelected(option)
                            ? "bg-secondary-soft"
                            : ""
                        }`}
                      >
                        {multiSelect && (
                          <div className="flex items-start gap-2.5 min-w-0">
                            <input
                              type="checkbox"
                              checked={isOptionSelected(option)}
                              onChange={() => {}}
                              onClick={(e) => handleCheckboxClick(e, option)}
                              className="h-4 w-4 shrink-0 cursor-pointer mt-0.5"
                            />
                            {renderOption ? (
                              renderOption(option)
                            ) : (
                              <p className="text-dark break-words">
                                {getOptionLabel(option)}
                              </p>
                            )}
                          </div>
                        )}

                        {!multiSelect &&
                          (renderOption ? (
                            renderOption(option)
                          ) : (
                            <p className="text-dark break-words">
                              {getOptionLabel(option)}
                            </p>
                          ))}
                      </div>
                      );
                    })
                  ) : (
                    <div className="p-3 text-center text-foreground">
                      No results found
                    </div>
                  )}
                </div>

                {/* Search bar - Show at bottom if dropdown is above */}
                {searchable && isAbove && (
                  <div className="p-2 border-t border-border sticky bottom-0 bg-card z-10">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-foreground w-4 h-4" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="pl-8 pr-2 py-2 w-full bg-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand text-foreground text-sm"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Categories - Only shown if showCategories is true and dropdown is above */}
                {showCategories && categories.length > 1 && isAbove && (
                  <div className="p-2 border-t border-border flex gap-2 overflow-x-auto sticky bottom-12 bg-card z-10">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedCategory === category
                            ? "bg-primary text-white"
                            : "bg-muted text-foreground hover:bg-secondary-soft"
                        }`}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Dropdown;
